import { useState, type ReactNode } from "react";
import { useTranslations } from "@/hooks/useTranslations";
import styles from "./InThisArticle.module.scss";

interface ISectionItem {
  id: string;
  title: string;
}

interface IInThisArticleProps {
  sections: ISectionItem[];
  title?: string;
  collapsedCount?: number;
}

const DEFAULT_COLLAPSED_COUNT = 3;

export function InThisArticle({ sections, title, collapsedCount = DEFAULT_COLLAPSED_COUNT }: IInThisArticleProps): ReactNode {
  const { t } = useTranslations();
  const [isExpanded, setIsExpanded] = useState(false);

  if (sections.length === 0) {
    return null;
  }

  const resolvedTitle = title ?? t("navigation.inThisArticle");
  const hasOverflow = sections.length > collapsedCount;
  const visibleSections = hasOverflow && !isExpanded ? sections.slice(0, collapsedCount) : sections;

  function handleSectionClick(sectionId: string): void {
    window.history.pushState(null, "", `#${sectionId}`);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function handleToggle(): void {
    setIsExpanded((prev) => !prev);
  }

  return (
    <nav className={styles.inThisArticle} aria-label={resolvedTitle}>
      <h2 className={styles.title}>{resolvedTitle}</h2>
      <ul className={styles.sectionList}>
        {visibleSections.map((section) => (
          <li key={section.id} className={styles.sectionItem}>
            <button
              type="button"
              className={styles.sectionLink}
              onClick={(): void => handleSectionClick(section.id)}
            >
              {section.title}
            </button>
          </li>
        ))}
      </ul>
      {hasOverflow && (
        <button
          type="button"
          className={styles.toggleButton}
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? t("common.showLess") : t("common.showMore")}
        >
          {isExpanded ? t("common.showLess") : t("common.showMore")}
        </button>
      )}
    </nav>
  );
}
